import store from '@/stores/store.js';
import { ModuleContext } from '@/stores/moduleConst.js';
import commonFn from '@/commons/commonFunction.js';
import taskManagerAPI from '@/apis/system/taskManagerAPI.js';
import { showError } from '@/commons/globalMessage.js';
import Command from '@/commons/wildcards/constants/Command.js';
import TaskType from '@/commons/wildcards/constants/TaskType.js';
import TaskStatus from '@/commons/wildcards/constants/TaskStatus.js';
import i18nBaseForm from '@/i18ns/vi/i18nBaseForm.js';
import i18nEnum from '@/i18ns/vi/i18nEnum';

class ValidateMaintainDatabase {
  getFormDateMaintainTime() {
    return this.getFormDateMaintain().getTime();
  }

  getFormDateMaintainDDMMYYYY() {
    return commonFn.getMoment(this.getFormDateMaintain()).format('DD/MM/YYYY');
  }

  getFormDateMaintain() {
    return new Date(sessionStorage.getItem(this.getKeyFromDateMaintain()));
  }

  setFormDateMaintain(fromDateMaintain) {
    sessionStorage.setItem(this.getKeyFromDateMaintain(), fromDateMaintain);
  }

  getToDateMaintainTime() {
    return this.getToDateMaintain().getTime();
  }

  getToDateMaintainDDMMYYYY() {
    return commonFn.getMoment(this.getToDateMaintain()).format('DD/MM/YYYY');
  }

  getToDateMaintain() {
    return new Date(sessionStorage.getItem(this.getKeyToDateMaintain()));
  }

  setToDateMaintain(toDateMaintain) {
    sessionStorage.setItem(this.getKeyToDateMaintain(), toDateMaintain);
  }

  getMaintainExpried() {
    return sessionStorage.getItem(this.getKeyMaintainExpried());
  }

  setMaintainExpried(expried) {
    sessionStorage.setItem(this.getKeyMaintainExpried(), expried);
  }

  setDatabaseIdMaintain(databaseId) {
    //Không truyền databaseId thì lấy databaseId của dữ liệu hiện tại
    databaseId = databaseId || store.state[ModuleContext].Context.DatabaseId;
    sessionStorage.setItem(this.getKeyDatabaseIdMaintain(), databaseId);
  }

  getDatabaseIdMaintain() {
    return sessionStorage.getItem(this.getKeyDatabaseIdMaintain());
  }

  getKeyMaintainExpried() {
    return 'mimosa_MaintainExpried_' + this.getDatabaseIdMaintain();
  }

  getKeyFromDateMaintain() {
    return 'mimosa_FromDateMaintain_' + this.getDatabaseIdMaintain();
  }

  getKeyToDateMaintain() {
    return 'mimosa_ToDateMaintain_' + this.getDatabaseIdMaintain();
  }

  getKeyDatabaseIdMaintain() {
    return 'mimosa_DatabaseIdMaintain';
  }

  clearInfoMaintainDatabase() {
    sessionStorage.removeItem(this.getKeyMaintainExpried());
    sessionStorage.removeItem(this.getKeyFromDateMaintain());
    sessionStorage.removeItem(this.getKeyToDateMaintain());
    sessionStorage.removeItem(this.getKeyDatabaseIdMaintain());
  }

  /**
   * Kiểm tra xem khoảng thời gian thực hiện chức năng có giao nhau với khoảng thời gian bảo trì dữ liệu không
   * @param {*} fromDate 
   * @param {*} toDate 
   * @returns true: có | false: không
   */
  checkDateActionIntersectionDateMaintain(fromDate, toDate) {
    let fromDateTime = new Date(commonFn.getMoment(fromDate).format('YYYY-MM-DD HH:mm:ss')).getTime();
    let toDateTime = new Date(commonFn.getMoment(toDate).format('YYYY-MM-DD HH:mm:ss')).getTime();
    let fromDateMaintainTime = this.getFormDateMaintainTime();
    let toDateMaintainTime = this.getToDateMaintainTime();
    return fromDateTime <= toDateMaintainTime && toDateTime >= fromDateMaintainTime;
  }

  /**
   * Kiểm tra xem có đang bảo trì dữ liệu không
   * @param {*} databaseId 
   * true: có | false: không
   * NVTUAN1 15.12.2023
   */
  async isMaintainedDatabase(databaseId) {

    //Cache lại thông tin databaseId của dữ liệu cần validate khi bảo trì dữ liệu để sử dụng chung
    this.setDatabaseIdMaintain(databaseId);

    //Kiểm tra trong cache xem có lệnh đang bảo trì không
    let now = new Date().getTime();
    let expried = this.getMaintainExpried();
    if (expried && expried > now) {
      return true;
    }

    //Trong cache không có lệnh bảo trì thì thực hiện call API lấy lệnh đang bảo trì dữ liệu nếu có và cập nhật vào sessionStorage
    try {
      commonFn.mask();
      let res = await taskManagerAPI.getTaskManagerLastest({ taskType: TaskType.ReBuild, taskStatus: TaskStatus.Running, databaseId: this.getDatabaseIdMaintain() });
      if (res && res.status === TaskStatus.Running) {
        let parameter = JSON.parse(res.parameter);
        let fromDateMaintain = commonFn.getMoment(parameter?.Parameter?.from_date).format('YYYY-MM-DD HH:mm:ss');
        let toDateMaintain = commonFn.getMoment(parameter?.Parameter?.to_date).format('YYYY-MM-DD HH:mm:ss');
        let expried = new Date().addMinutes(10).getTime();

        this.setMaintainExpried(expried);
        this.setFormDateMaintain(fromDateMaintain);
        this.setToDateMaintain(toDateMaintain);

        return true;
      }
      return false;
    } catch (error) {
      console.log(error);
      return false;
    } finally {
      commonFn.unmask();
    }
  }

  /**
   * Validate thực hiện chức năng trong khi bảo trì dữ liệu
   * @param {*} command 
   * @param {*} data 
   * @param {*} databaseId 
   * @param {*} databaseName 
   * NVTUAN1 15.12.2023
   */
  async validateMaintainDatabase(command, data, databaseId, databaseName) {
    let isValid = await this.isMaintainedDatabase(databaseId);

    if (!isValid) {
      return true;
    }

    let mes = {};
    let commandName = i18nEnum.Command[command];
    let fromDateMaintain = this.getFormDateMaintainDDMMYYYY();
    let toDateMaintain = this.getToDateMaintainDDMMYYYY();
    //Không truyền databaseName thì lấy databaseName của dữ liệu hiện tại
    databaseName = databaseName || store.state[ModuleContext].Context.DatabaseName;

    switch (command) {
      //Bỏ ghi
      case Command.UnPosted:
      //Ghi sổ
      case Command.Posted:
        mes = this.validateMaintainDatabaseWhenUnPostedOrPosted(commandName, data, fromDateMaintain, toDateMaintain, databaseName);
        break;
      //Bỏ ghi theo lô
      case Command.UnPostBatch:
      //Ghi sổ theo lô
      case Command.PostBatch:
        mes = this.validateMaintainDatabaseWhenUnPostBatchOrPostBatch(commandName, data, fromDateMaintain, toDateMaintain, databaseName);
        break;
      //Bảng kê có chức năng thanh toán
      case Command.PayStatements:
      case Command.CancelPayStatements:
        mes = this.validateMaintainDatabaseWhenPayStatements(commandName, data, fromDateMaintain, toDateMaintain, databaseName);
        break;
      //Thêm/Sửa danh mục, chứng từ (Chứng từ ghi sổ, Bảng tổng hợp chứng từ cùng loại)
      case Command.Edit:
      case Command.Add:
        mes = {
          title: i18nBaseForm.ValidateMaintainDatabase.Title.format(commandName),
          message: i18nBaseForm.ValidateMaintainDatabase.ContentAction.format(fromDateMaintain, toDateMaintain, databaseName, commandName.toLowerCase(), data)
        };
        break;
      //Sắp xếp lại số chứng từ
      case Command.SortRefNo:
      //Tính giá xuất kho
      case Command.CalclnInOutward:
      //Sao chép dữ liệu
      case Command.Copy:
        mes = this.validateMaintainDatabaseWhenDateActionIntersectionDateMaintain(commandName, data, fromDateMaintain, toDateMaintain, databaseName);
        break;
      //Các chứng năng khác
      default:
        mes = {
          title: i18nBaseForm.ValidateMaintainDatabase.Title.format(commandName),
          message: i18nBaseForm.ValidateMaintainDatabase.Content.format(fromDateMaintain, toDateMaintain, databaseName, commandName)
        };
        break;
    }

    if (!mes || Object.keys(mes).length === 0) {
      return true;
    }

    showError(mes.message, mes.title, 500, 'Đóng');

    return false;
  }

  /**
   * Thực hiện validate khi thực hiện bỏ ghi sổ/ghi sổ trong khi bảo trì dữ liệu
   * @param {*} commandName 
   * @param {*} data 
   * @param {*} fromDateMaintain 
   * @param {*} toDateMaintain 
   * @param {*} databaseName 
   * NVTUAN1 15.12.2023
   */
  validateMaintainDatabaseWhenUnPostedOrPosted(commandName, data, fromDateMaintain, toDateMaintain, databaseName) {
    let fromDateMaintainTime = this.getFormDateMaintainTime();
    let toDateMaintainTime = this.getToDateMaintainTime();
    let mes = {};
    let records = [];

    if (Array.isArray(data)) {
      records = records.concat(data);
    } else if (typeof data === 'object' && data) {
      records.push(data);
    }

    //Không có bản ghi nào thì return luôn
    if (records.length === 0) {
      return mes;
    }

    // Lấy ra các bản ghi có ngày ghi sổ nằm ngoài thời gian bảo trì dữ liệu
    records = records.filter(x => !x.posted_date || new Date(x.posted_date).getTime() < fromDateMaintainTime || new Date(x.posted_date).getTime() > toDateMaintainTime);

    //Không có bản ghi nào nằm ngoài thời gian bảo trì dữ liệu thì show cảnh báo
    if (records.length === 0) {
      mes = {
        title: i18nBaseForm.ValidateMaintainDatabase.Title.format(commandName),
        message: i18nBaseForm.ValidateMaintainDatabase.Content.format(fromDateMaintain, toDateMaintain, databaseName, commandName)
      };
    }

    return mes;
  }

  /**
   * Thực hiện validate khi thực hiện ghi sổ/bỏ ghi sổ theo lô trong khi bảo trì dữ liệu
   * @param {*} commandName 
   * @param {*} data 
   * @param {*} fromDateMaintain 
   * @param {*} toDateMaintain 
   * @param {*} databaseName 
   * NVTUAN1 15.12.2023
   */
  validateMaintainDatabaseWhenUnPostBatchOrPostBatch(commandName, data, fromDateMaintain, toDateMaintain, databaseName) {
    let mes = {};
    if (this.checkDateActionIntersectionDateMaintain(data?.fromDate, data?.toDate)) {
      mes = {
        title: i18nBaseForm.ValidateMaintainDatabase.Title.format(commandName),
        message: i18nBaseForm.ValidateMaintainDatabase.Content.format(fromDateMaintain, toDateMaintain, databaseName, commandName)
      };
    }

    return mes;
  }

  /**
   * Thực hiện validate khi thực hiện các chức năng có khoảng thời gian giao nhau với thời gian bảo trì dữ liệu
   * @param {*} commandName 
   * @param {*} data 
   * @param {*} fromDateMaintain 
   * @param {*} toDateMaintain 
   * @param {*} databaseName 
   * NVTUAN1 20.12.2023
   */
  validateMaintainDatabaseWhenDateActionIntersectionDateMaintain(commandName, data, fromDateMaintain, toDateMaintain, databaseName) {
    let mes = {};
    if (this.checkDateActionIntersectionDateMaintain(data?.fromDate, data?.toDate)) {
      mes = {
        title: i18nBaseForm.ValidateMaintainDatabase.Title.format(commandName),
        message: i18nBaseForm.ValidateMaintainDatabase.Content.format(fromDateMaintain, toDateMaintain, databaseName, commandName)
      };
    }

    return mes;
  }

  /**
   * Thực hiện validate khi thực hiện thanh toán hóa đơn
   * @param {*} commandName 
   * @param {*} data 
   * @param {*} fromDateMaintain 
   * @param {*} toDateMaintain 
   * @param {*} databaseName 
   * NNKiem 19/12/2023
   */
  validateMaintainDatabaseWhenPayStatements(commandName, data, fromDateMaintain, toDateMaintain, databaseName) {
    let refDateTime = data?.ref_date?.getTime() ?? new Date().getTime();
    let fromDateMaintainTime = this.getFormDateMaintainTime();
    let toDateMaintainTime = this.getToDateMaintainTime();
    let mes = {};
    //Nếu ngày chứng từ nằm trong khoảng thời gian bảo trì dữ liệu thì trả về câu cảnh báo
    if (refDateTime >= fromDateMaintainTime && refDateTime <= toDateMaintainTime) {
      mes = {
        title: i18nBaseForm.ValidateMaintainDatabase.Title.format(commandName),
        message: i18nBaseForm.ValidateMaintainDatabase.ContentAction.format(fromDateMaintain, toDateMaintain, databaseName, commandName.toLowerCase(), data?.ref_no)
      };
    }

    return mes;
  }
}

export default new ValidateMaintainDatabase();